import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar, Download, Lightbulb } from 'lucide-react';
import { toast } from 'sonner';

export default function TimelineCalculatorTab() {
  const [publicationDate, setPublicationDate] = useState('');
  const [procurementType, setProcurementType] = useState('goods');
  const [requiresHigherAuthority, setRequiresHigherAuthority] = useState(false);
  const [includePreProcurement, setIncludePreProcurement] = useState(false);
  const [includePreBid, setIncludePreBid] = useState(true);
  const [timeline, setTimeline] = useState(null);

  const goodsInfraTimeline = [
    { activity: 'Pre-Procurement Conference', minDays: 1, operationalDays: 1 },
    { activity: 'Advertising/Publication/Posting', minDays: 7, operationalDays: 7 },
    { activity: 'Pre-Bid Conference', minDays: 12, operationalDays: 12 },
    { activity: 'Bid Opening', minDays: 1, operationalDays: 1 },
    { activity: 'Bid Evaluation', minDays: 1, operationalDays: 7 },
    { activity: 'Post-Qualification', minDays: 2, operationalDays: 12 },
    { activity: 'BAC Resolution/Notice of Award', minDays: 1, operationalDays: 10 },
    { activity: 'Contract Preparation', minDays: 1, operationalDays: 10 },
    { activity: 'Higher Authority Approval', minDays: 1, operationalDays: 15 },
    { activity: 'Notice to Proceed', minDays: 1, operationalDays: 3 }
  ];

  const consultingTimeline = [
    { activity: 'Advertisement', minDays: 7, operationalDays: 7 },
    { activity: 'Submission of EOI', minDays: 7, operationalDays: 7 },
    { activity: 'Shortlisting', minDays: 3, operationalDays: 5 },
    { activity: 'Pre-Bid Conference', minDays: 7, operationalDays: 7 },
    { activity: 'Submission of Proposals', minDays: 14, operationalDays: 21 },
    { activity: 'Evaluation', minDays: 7, operationalDays: 14 },
    { activity: 'Post-Qualification', minDays: 5, operationalDays: 10 },
    { activity: 'Notice of Award', minDays: 1, operationalDays: 5 },
    { activity: 'Contract Signing', minDays: 1, operationalDays: 7 },
    { activity: 'Notice to Proceed', minDays: 1, operationalDays: 3 }
  ];

  const calculateTimeline = () => {
    if (!publicationDate) return;

    const startDate = new Date(publicationDate);
    let timelineData = procurementType === 'consulting' ? consultingTimeline : goodsInfraTimeline;
    
    // Filter out Pre-Procurement Conference if not included
    if (!includePreProcurement) {
      timelineData = timelineData.filter(item => item.activity !== 'Pre-Procurement Conference');
    }
    
    // Filter out Pre-Bid Conference if not included
    if (!includePreBid) {
      timelineData = timelineData.filter(item => item.activity !== 'Pre-Bid Conference');
    }
    
    // Filter out Higher Authority Approval if not required
    if (!requiresHigherAuthority) {
      timelineData = timelineData.filter(item => item.activity !== 'Higher Authority Approval');
    }
    
    const results = [];
    let currentDate = new Date(startDate);

    timelineData.forEach((item) => {
      const startDateForActivity = new Date(currentDate);
      currentDate.setDate(currentDate.getDate() + item.operationalDays);
      const endDateForActivity = new Date(currentDate);

      results.push({
        activity: item.activity,
        startDate: startDateForActivity.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }),
        endDate: endDateForActivity.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }),
        days: item.operationalDays
      });
    });

    setTimeline(results);
  };

  const exportTimeline = () => {
    if (!timeline) {
      toast.error('No timeline to export');
      return;
    }

    try {
      toast.info('Generating export...');
      
      // Generate text content for the timeline
      let textContent = '='.repeat(70) + '\n';
      textContent += 'PROCUREMENT TIMELINE CALCULATOR - SUMMARY\n';
      textContent += '='.repeat(70) + '\n\n';
      
      textContent += `Procurement Type: ${procurementType === 'consulting' ? 'Consulting Services' : 'Goods & Infrastructure'}\n`;
      textContent += `Publication Date: ${new Date(publicationDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}\n`;
      textContent += `Pre-Procurement Conference: ${includePreProcurement ? 'Included' : 'Not Included'}\n`;
      textContent += `Pre-Bid Conference: ${includePreBid ? 'Included' : 'Not Included'}\n`;
      textContent += `Higher Authority Approval: ${requiresHigherAuthority ? 'Required (Included)' : 'Not Required (Excluded)'}\n`;
      textContent += `Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}\n\n`;
      
      textContent += '='.repeat(70) + '\n';
      textContent += 'TIMELINE ACTIVITIES\n';
      textContent += '='.repeat(70) + '\n\n';
      
      // Add timeline data
      timeline.forEach((item, index) => {
        textContent += `${index + 1}. ${item.activity}\n`;
        textContent += `   Start Date: ${item.startDate}\n`;
        textContent += `   End Date: ${item.endDate}\n`;
        textContent += `   Duration: ${item.days} Calendar Days\n`;
        textContent += '\n';
      });
      
      textContent += '='.repeat(70) + '\n';
      textContent += `Total Activities: ${timeline.length}\n`;
      const totalDays = timeline.reduce((sum, item) => sum + item.days, 0);
      textContent += `Total Duration: ${totalDays} Calendar Days\n`;
      textContent += '='.repeat(70) + '\n\n';
      
      textContent += 'Note: This timeline is generated based on RA 12009 and its IRR.\n';
      textContent += 'Always consult the complete law and implementing rules for specific situations.\n';
      
      // Create and download text file
      const blob = new Blob([textContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Procurement_Timeline_${procurementType}_${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Timeline exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export timeline');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-emerald-600" />
          Procurement Timeline Calculator
        </CardTitle>
        <CardDescription>
          Calculate all activity dates for competitive bidding based on publication date
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Input Section */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="publicationDate">Publication Date</Label>
              <Input
                id="publicationDate"
                type="date"
                value={publicationDate}
                onChange={(e) => setPublicationDate(e.target.value)}
                className="cursor-pointer"
              />
              <p className="text-xs text-slate-500">Click to select date from calendar</p>
            </div>
            <div className="space-y-2">
              <Label>Procurement Type</Label>
              <Tabs value={procurementType} onValueChange={setProcurementType}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="goods">Goods & Infra</TabsTrigger>
                  <TabsTrigger value="consulting">Consulting</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>

          {/* Pre-Procurement Conference Option */}
          <div className="flex items-start space-x-3 p-4 border rounded-lg bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700">
            <Checkbox
              id="preProcurement"
              checked={includePreProcurement}
              onCheckedChange={setIncludePreProcurement}
              className="mt-1"
            />
            <div className="flex-1">
              <Label 
                htmlFor="preProcurement" 
                className="cursor-pointer font-medium text-sm"
              >
                Include Pre-Procurement Conference
              </Label>
              <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                <span className="font-medium">Optional for:</span>
              </p>
              <ul className="text-xs text-slate-600 dark:text-slate-400 mt-1 ml-4 space-y-0.5">
                <li>• Goods costing ₱5 million or below</li>
                <li>• Infrastructure costing ₱10 million or below</li>
                <li>• Consulting Services costing ₱2 million or below</li>
              </ul>
              <p className="text-xs text-slate-500 dark:text-slate-500 mt-2 italic">
                Pursuant to Section 49.1 of RA 12009 IRR. Check this to include in timeline (adds 1 calendar day).
              </p>
            </div>
          </div>

          {/* Pre-Bid Conference Option */}
          <div className="flex items-start space-x-3 p-4 border rounded-lg bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700">
            <Checkbox
              id="preBid"
              checked={includePreBid}
              onCheckedChange={setIncludePreBid}
              className="mt-1"
            />
            <div className="flex-1">
              <Label 
                htmlFor="preBid" 
                className="cursor-pointer font-medium text-sm"
              >
                Include Pre-Bid Conference
              </Label>
              <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                <span className="font-medium">Required for ABC of ₱3,000,000.00 or more</span>
              </p>
              <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                For procurement projects with ABC less than ₱3 million, pre-bid conferences may be conducted at the discretion of the BAC or upon written request of prospective bidders.
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-500 mt-2 italic">
                Applies to Competitive Bidding, Competitive Dialogue, Limited Source Bidding, and Unsolicited Offer with Bid Matching. Uncheck to exclude from timeline (removes 12 calendar days for Goods/Infra, 7 days for Consulting).
              </p>
            </div>
          </div>

          {/* Higher Authority Approval Option */}
          <div className="flex items-start space-x-3 p-4 border rounded-lg bg-slate-50 dark:bg-slate-800">
            <Checkbox
              id="higherAuthority"
              checked={requiresHigherAuthority}
              onCheckedChange={setRequiresHigherAuthority}
              className="mt-1"
            />
            <div className="flex-1">
              <Label 
                htmlFor="higherAuthority" 
                className="cursor-pointer font-medium text-sm"
              >
                Requires Higher Authority Approval
              </Label>
              <p className="text-xs text-slate-500 mt-1">
                Check this if your procurement requires approval from higher authority (e.g., projects above certain thresholds or requiring special authorization). This adds 15 calendar days to the timeline.
              </p>
            </div>
          </div>

          <Button onClick={calculateTimeline} className="w-full bg-emerald-600 hover:bg-emerald-700">
            Calculate Timeline
          </Button>

          {/* Results Section */}
          {timeline && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Calculated Timeline</h3>
                <Button variant="outline" size="sm" onClick={exportTimeline}>
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </div>
              
              {/* Mobile scroll hint */}
              <p className="text-xs text-slate-500 md:hidden flex items-center gap-1">
                <Lightbulb className="w-3 h-3 flex-shrink-0" />
                Scroll horizontally to see all columns →
              </p>
              
              {/* Horizontally scrollable container for mobile */}
              <div className="border rounded-lg overflow-x-auto">
                <table className="w-full min-w-[600px]">
                  <thead className="bg-slate-100 dark:bg-slate-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium whitespace-nowrap">Activity</th>
                      <th className="px-4 py-3 text-left text-sm font-medium whitespace-nowrap">Start Date</th>
                      <th className="px-4 py-3 text-left text-sm font-medium whitespace-nowrap">End Date</th>
                      <th className="px-4 py-3 text-left text-sm font-medium whitespace-nowrap">Days</th>
                    </tr>
                  </thead>
                  <tbody>
                    {timeline.map((item, index) => (
                      <tr key={index} className="border-t">
                        <td className="px-4 py-3 text-sm whitespace-nowrap">{item.activity}</td>
                        <td className="px-4 py-3 text-sm whitespace-nowrap">{item.startDate}</td>
                        <td className="px-4 py-3 text-sm font-medium text-emerald-600 whitespace-nowrap">{item.endDate}</td>
                        <td className="px-4 py-3 text-sm whitespace-nowrap">{item.days} CD</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
